import "./CalculatedPayrollDataGridStyle.css";

import { Box, CircularProgress, Tooltip, Typography, useTheme } from "@mui/material";
import {
	EnhancedButtonPrimeReact,
	EnhancedButtonPrimeReactSeverity,
	EnhancedDropDownRHF,
	RHookFormNumberField,
	RequestParams,
} from "@websential/cosmic-v20";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
	clearStickyCheckboxHighlight,
	refreshStickyGrid,
	registerStickyGrid,
	unregisterStickyGrid,
} from "../stickyHeaderManager";
import { formatAmountCommaSeperator, formatDate, formatDateToShow, formatThousandSeperatorWithTwoFixedAmount, getValueByFieldPath } from "../../../common/Common";

import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Checkbox } from "primereact/checkbox";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import InfoIcon from '@mui/icons-material/Info';
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { MultiSelect } from "primereact/multiselect";
import { Skeleton } from "primereact/skeleton";
import isEqual from "lodash/isEqual";

export interface ICalculatedPayrollDataGrid {
	rows: any;
	columns: any;
	showIndexColumn: boolean;
	showChecboxColumn: boolean;
	onRowDeleteClicked?: (params: any) => void;
	onRowEditClicked?: (params: any) => void;
	isLoading?: any;
	onSnackbarMessageData?: (value: any) => void;
	onRequestParamsChange?: (params: RequestParams) => void;
	initialPageSize?: number | undefined;
	initialPage?: number;
	totalRecords?: number;
	selectionToolbar?: (selectedItems: any) => React.ReactNode;
	selectedRowsValues?: any;
	changeLoginForGetEmptyItemsBySearchQuery?: any;
	searchFieldPlaceholder?: string;
	showGlobalSearch?: boolean;
	tableMargin?: string;
	rowsPerPageOptions?: number[];
	footer?: any;
	whiteSpace?: string;
	requestParamsToInitial?: boolean;
	tableScrollHeight?: boolean;
	pageTitle?: string;
	PayrollTableData: (data: any) => void;
	selectedPayPeriod: any
	clearAllSelectedItemsForCalculatedPayrollTable: string | number;
	pageScrollContainerRef?: React.RefObject<HTMLElement>;
	gridsMaxTotalWidth?: any;
	enableFrozenColumns?: boolean;
}

const placeCaretBeforeDecimal = (el: HTMLElement) => {
	const text = el.textContent ?? "";
	const decimalIndex = text.indexOf(".");
	const offset = decimalIndex >= 0 ? decimalIndex : text.length;

	const textNode = el.firstChild;
	const range = document.createRange();
	const sel = window.getSelection();

	if (textNode && textNode.nodeType === Node.TEXT_NODE) {
		const safeOffset = Math.min(offset, textNode.textContent?.length ?? 0);
		range.setStart(textNode, safeOffset);
		range.collapse(true);
	} else {
		range.selectNodeContents(el);
		range.collapse(false);
	}

	sel?.removeAllRanges();
	sel?.addRange(range);
};



// const NumberCell = React.memo(function NumberCell({
// 	rowIndex,
// 	field,
// 	employeeId,
// 	displayValue,
// 	className,
// 	registerRef,
// 	column,        // CHANGED: needs the column object for handleCellNavigation
// 	onCellKeyDown, // CHANGED: passed from parent, bound to handleCellNavigation
// }: any) {
// 	const cellRef: any = useRef<HTMLDivElement | null>(null);

// 	useLayoutEffect(() => {
// 		const el = cellRef.current;
// 		if (el && document.activeElement !== el && el.textContent !== displayValue) {
// 			el.textContent = displayValue;
// 		}
// 	}, [displayValue]);

// 	return (
// 		<div
// 			ref={(el) => {
// 				cellRef.current = el;
// 				registerRef(el);
// 			}}
// 			data-payroll-input="true"
// 			data-row-index={rowIndex}
// 			data-field={field}
// 			data-employee-id={employeeId}
// 			contentEditable
// 			suppressContentEditableWarning
// 			className={className}
// 			style={{ textAlign: "right", outline: "none", whiteSpace: "nowrap" }}
// 			onFocus={(e) => {
// 				// CHANGED: place caret before the decimal, matching input behavior
// 				requestAnimationFrame(() => placeCaretBeforeDecimal(e.currentTarget));
// 			}}
// 			onKeyDown={(e) => {
// 				// CHANGED: arrow-key navigation, matching input behavior
// 				onCellKeyDown && onCellKeyDown(e, rowIndex, column);
// 			}}
// 			onInput={(e) => {
// 				const el = e.currentTarget;
// 				let value = el.textContent || "";
// 				value = value.replace(/[^\d.-]/g, "");
// 				const parts = value.split(".");
// 				if (parts.length > 2) value = `${parts[0]}.${parts.slice(1).join("")}`;
// 				const decimalParts = value.split(".");
// 				if (decimalParts.length === 2) {
// 					value = `${decimalParts[0]}.${decimalParts[1].slice(0, 2)}`;
// 				}
// 				if (value !== el.textContent) {
// 					const sel = window.getSelection();
// 					el.textContent = value;
// 					const range = document.createRange();
// 					range.selectNodeContents(el);
// 					range.collapse(false);
// 					sel?.removeAllRanges();
// 					sel?.addRange(range);
// 				}
// 			}}
// 		>
// 			{displayValue}
// 		</div>
// 	);
// },
// 	(prev, next) =>
// 		prev.displayValue === next.displayValue &&
// 		prev.className === next.className &&
// 		prev.employeeId === next.employeeId &&
// 		prev.field === next.field &&
// 		prev.rowIndex === next.rowIndex
// );


type CellType = "number" | "text";

const sanitizeNumberInput = (raw: string) => {
	let value = raw.replace(/[^\d.-]/g, "");
	const parts = value.split(".");
	if (parts.length > 2) value = `${parts[0]}.${parts.slice(1).join("")}`;
	const decimalParts = value.split(".");
	if (decimalParts.length === 2) {
		value = `${decimalParts[0]}.${decimalParts[1].slice(0, 2)}`;
	}
	return value;
};

const EditableCell = React.memo(function EditableCell({
	cellType,
	rowIndex,
	field,
	employeeId,
	displayValue,
	className,
	registerRef,
	column,
	onCellKeyDown,
}: {
	cellType: CellType;
	rowIndex: number;
	field: string;
	employeeId: any;
	displayValue: string;
	className?: string;
	registerRef: (el: HTMLInputElement | null) => void | any;
	column: any;
	onCellKeyDown?: (e: React.KeyboardEvent<any>, rowIndex: number, column: any) => void;
}) {
	const cellRef = useRef<HTMLDivElement | null>(null);

	// useLayoutEffect(() => {
	// 	const el = cellRef.current;
	// 	if (el && document.activeElement !== el && el.textContent !== displayValue) {
	// 		el.textContent = displayValue;
	// 	}
	// }, [displayValue]);

	useLayoutEffect(() => {
		const el = cellRef.current;

		if (!el) return;

		// Don't overwrite what the user is currently editing
		if (document.activeElement === el) return;

		if (el.textContent !== displayValue) {
			el.textContent = displayValue;
		}
	}, [displayValue]);

	return (
		<div
			ref={(el: any) => {
				cellRef.current = el;
				registerRef(el);
			}}
			data-payroll-input="true"
			data-cell-type={cellType}
			data-row-index={rowIndex}
			data-field={field}
			data-employee-id={employeeId}
			contentEditable
			suppressContentEditableWarning
			className={className}
			style={{
				textAlign: cellType === "number" ? "right" : "left",
				outline: "none",
				whiteSpace: "nowrap",
			}}
			// onFocus={(e) => {
			// 	if (cellType === "number") {
			// 		requestAnimationFrame(() => placeCaretBeforeDecimal(e.currentTarget));
			// 	}
			// }}
			onFocus={(e) => {
				if (cellType !== "number") return;

				const el = e.currentTarget;

				// Convert formatted display to editable raw number
				const rawValue = (el.textContent || "").replace(/,/g, "");

				if (el.textContent !== rawValue) {
					el.textContent = rawValue;
				}

				requestAnimationFrame(() => {
					placeCaretBeforeDecimal(el);
				});
			}}
			onKeyDown={(e) => {
				onCellKeyDown && onCellKeyDown(e, rowIndex, column);
			}}
			onInput={(e) => {
				if (cellType !== "number") return; // plain text needs no live sanitization
				const el = e.currentTarget;
				const sanitized = sanitizeNumberInput(el.textContent || "");
				if (sanitized !== el.textContent) {
					const sel = window.getSelection();
					el.textContent = sanitized;
					const range = document.createRange();
					range.selectNodeContents(el);
					range.collapse(false);
					sel?.removeAllRanges();
					sel?.addRange(range);
				}
			}}
		>
			{displayValue}
		</div>
	);
},
	(prev, next) =>
		prev.cellType === next.cellType &&
		prev.displayValue === next.displayValue &&
		prev.className === next.className &&
		prev.employeeId === next.employeeId &&
		prev.field === next.field &&
		prev.rowIndex === next.rowIndex
);

// const DateCell = React.memo(function DateCell({
// 	rowIndex,
// 	field,
// 	employeeId,
// 	isoValue,
// 	displayValue,
// 	registerRef,
// 	onChangeDate,
// }: {
// 	rowIndex: number;
// 	field: string;
// 	employeeId: any;
// 	isoValue: string;
// 	displayValue: string;
// 	registerRef: (el: HTMLInputElement | null) => void;
// 	onChangeDate: (rowIndex: number, date: Date | null) => void;
// }) {
// 	const labelRef = useRef<HTMLSpanElement | null>(null);

// 	useLayoutEffect(() => {
// 		if (labelRef.current && labelRef.current.textContent !== displayValue) {
// 			labelRef.current.textContent = displayValue;
// 		}
// 	}, [displayValue]);

// 	return (
// 		<span className="payroll-date-input-wrapper">
// 			<input
// 				ref={registerRef}
// 				data-payroll-input="true"
// 				data-cell-type="date"
// 				data-row-index={rowIndex}
// 				data-field={field}
// 				data-employee-id={employeeId}
// 				type="date"
// 				defaultValue={isoValue}
// 				className="payroll-date-input"
// 				onClick={(e) => {
// 					const input = e.currentTarget;
// 					if (input.showPicker) {
// 						try { input.showPicker(); } catch { /* already open */ }
// 					}
// 				}}
// 				onChange={(e) => {
// 					const raw = e.currentTarget.value;
// 					if (!raw) {
// 						onChangeDate(rowIndex, null);
// 						return;
// 					}
// 					const [year, month, day] = raw.split("-").map(Number);
// 					onChangeDate(rowIndex, new Date(year, month - 1, day));
// 				}}
// 			/>
// 			<span className="payroll-date-display" ref={labelRef}>{displayValue}</span>
// 		</span>
// 	);
// },
// 	(prev, next) =>
// 		prev.isoValue === next.isoValue &&
// 		prev.displayValue === next.displayValue &&
// 		prev.employeeId === next.employeeId &&
// 		prev.field === next.field &&
// 		prev.rowIndex === next.rowIndex
// );


function sanitizeDateInput(raw: string): string {
	// keep only digits and "/", collapse multiple slashes, cap length
	let cleaned = raw.replace(/[^\d/]/g, "");
	cleaned = cleaned.replace(/\/{2,}/g, "/");
	return cleaned.slice(0, 10); // MM/DD/YYYY max length
}

function parseTypedDate(raw: string): Date | null {
	const trimmed = raw.trim();
	if (!trimmed) return null;

	// expects MM/DD/YYYY (adjust to your formatDateToShow format)
	const match = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
	if (!match) return null;

	const [, mm, dd, yyyy] = match;
	const month = Number(mm);
	const day = Number(dd);
	const year = Number(yyyy);
	const date = new Date(year, month - 1, day);

	// guard against invalid dates like 02/31/2024 rolling over
	if (
		date.getFullYear() !== year ||
		date.getMonth() !== month - 1 ||
		date.getDate() !== day
	) {
		return null;
	}
	return date;
}

const DateCell = React.memo(function DateCell({
	rowIndex,
	field,
	employeeId,
	displayValue,
	registerRef,
	onChangeDate,
	onCellKeyDown,
	column,
}: {
	rowIndex: number;
	field: string;
	employeeId: any;
	displayValue: string;
	registerRef: (el: HTMLInputElement | null) => void | any;
	onChangeDate: (rowIndex: number, date: Date | null) => void;
	onCellKeyDown?: (e: React.KeyboardEvent<any>, rowIndex: number, column: any) => void;
	column?: any;
}) {
	const cellRef = useRef<HTMLDivElement | null>(null);

	useLayoutEffect(() => {
		const el = cellRef.current;
		if (el && document.activeElement !== el && el.textContent !== displayValue) {
			el.textContent = displayValue;
		}
	}, [displayValue]);

	const commitValue = (el: HTMLDivElement) => {
		const raw = el.textContent || "";
		const parsed = parseTypedDate(raw);
		// snap the div back to the canonical displayValue on next render;
		// if parse failed and raw wasn't empty, leave the invalid text as-is
		// so the user can see/fix what they typed
		onChangeDate(rowIndex, parsed);
	};

	return (
		<div
			ref={(el: any) => {
				cellRef.current = el;
				registerRef(el);
			}}
			data-payroll-input="true"
			data-cell-type="date"
			data-row-index={rowIndex}
			data-field={field}
			data-employee-id={employeeId}
			contentEditable
			suppressContentEditableWarning
			className="payroll-date-cell"
			style={{
				textAlign: "left",
				outline: "none",
				whiteSpace: "nowrap",
			}}
			onInput={(e) => {
				const el = e.currentTarget;
				const sanitized = sanitizeDateInput(el.textContent || "");
				if (sanitized !== el.textContent) {
					const sel = window.getSelection();
					el.textContent = sanitized;
					const range = document.createRange();
					range.selectNodeContents(el);
					range.collapse(false);
					sel?.removeAllRanges();
					sel?.addRange(range);
				}
			}}
			onBlur={(e) => commitValue(e.currentTarget)}
			onKeyDown={(e) => {
				if (e.key === "Enter") {
					e.preventDefault();
					commitValue(e.currentTarget);
					(e.currentTarget as HTMLDivElement).blur();
				}
				onCellKeyDown && onCellKeyDown(e, rowIndex, column);
			}}
		>
			{displayValue}
		</div>
	);
},
	(prev, next) =>
		prev.displayValue === next.displayValue &&
		prev.employeeId === next.employeeId &&
		prev.field === next.field &&
		prev.rowIndex === next.rowIndex
);
export const CalculatedPayrollDataGrid: React.FC<ICalculatedPayrollDataGrid> = ({
	rows,
	columns,
	showIndexColumn,
	showChecboxColumn,
	onRowDeleteClicked,
	onRowEditClicked,
	isLoading,
	onSnackbarMessageData,
	onRequestParamsChange,
	initialPageSize = 10,
	initialPage = 0,
	totalRecords,
	selectionToolbar,
	selectedRowsValues,
	searchFieldPlaceholder,
	showGlobalSearch = true,
	tableMargin,
	rowsPerPageOptions,
	changeLoginForGetEmptyItemsBySearchQuery,
	footer,
	whiteSpace,
	requestParamsToInitial,
	tableScrollHeight,
	pageTitle,
	PayrollTableData,
	selectedPayPeriod,
	clearAllSelectedItemsForCalculatedPayrollTable,
	pageScrollContainerRef,
	gridsMaxTotalWidth,
	enableFrozenColumns = false,
}) => {
	const isInitialRequest = useRef(true);
	const [loading, setLoading] = useState(false);
	const [payrollItems, setPayrollItems] = useState<any>(
		rows && rows
	);

	const payrollItemsRef = useRef(payrollItems);
	// const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});
	const inputRefs = useRef<Record<string, HTMLElement | null>>({});

	// useEffect(() => {
	// 	PayrollTableData(payrollItems);

	// }, [payrollItems]);

	// useEffect(() => {
	// 	if (!isEqual(rows, payrollItems)) {
	// 		setPayrollItems([...rows]);
	// 	}
	// }, [rows]);

	// const notifyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	// useEffect(() => {
	// 	if (notifyTimerRef.current) clearTimeout(notifyTimerRef.current);
	// 	notifyTimerRef.current = setTimeout(() => {
	// 		PayrollTableData(payrollItemsRef.current);
	// 	}, 300);
	// 	return () => { if (notifyTimerRef.current) clearTimeout(notifyTimerRef.current); };
	// }, [payrollItems]);

	const notifyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const lastNotifiedRef = useRef<any>(null);
	useEffect(() => {
		if (notifyTimerRef.current) clearTimeout(notifyTimerRef.current);

		notifyTimerRef.current = setTimeout(() => {
			if (isEqual(lastNotifiedRef.current, payrollItemsRef.current)) return;
			lastNotifiedRef.current = payrollItemsRef.current;
			PayrollTableData(payrollItemsRef.current);
		}, 300);

		return () => {
			if (notifyTimerRef.current) clearTimeout(notifyTimerRef.current);
		};
	}, [payrollItems]);

	useEffect(() => {
		payrollItemsRef.current = payrollItems;
	}, [payrollItems]);

	// useEffect(() => {
	// 	setPayrollItems(rows ?? []);
	// }, [rows]);

	const lastRowsRef = useRef(rows);
	useEffect(() => {
		if (isEqual(lastRowsRef.current, rows)) {
			return;
		}
		lastRowsRef.current = rows;
		setPayrollItems(rows ?? []);
	}, [rows]);

	const sortedColumns = useMemo(
		() =>
			columns
				? [...columns].sort(
					(a: any, b: any) =>
						(a.sequence ?? 0) -
						(b.sequence ?? 0)
				)
				: [],
		[columns]
	);

	const theme = useTheme();
	const [itemSearch, setItemSearch] = useState("");
	const [requestParams, setRequestParams] = useState<any>({
		first: initialPage,
		rows: initialPageSize,
		page: initialPage,
		sortField: null,
		sortOrder: "asc",
		searchQuery: "",
		filters: {
			name: { value: "", matchMode: "contains" },
			"country.name": { value: "", matchMode: "contains" },
			company: { value: "", matchMode: "contains" },
			"representative.name": { value: "", matchMode: "contains" },
		},
	});

	const [selectedItems, setSelectedItems] = useState<any>([]);
	const [selectedItemsIds, setSelectedItemsIds] = useState<any>([]);
	const [itemSearsch, setItemSearsch] = useState("");
	// this is for select all checkbox in footer bottom
	const [manualSelectAll, setManualSelectAll] = useState<any>(false);
	const onSearchButtonClicked = () => {
		// setLoading(true);
		setRequestParams((prevParams: any) => ({
			...prevParams,
			first: initialPage,
			rows: initialPageSize,
			page: initialPage,
			searchQuery: itemSearch,
		}));
		if (changeLoginForGetEmptyItemsBySearchQuery) {
			changeLoginForGetEmptyItemsBySearchQuery(false);
		}
		// setTimeout(() => {
		// 	setLoading(false);
		// }, 1000);
	};
	const handleClearSearch = () => {
		// setLoading(true);
		setItemSearch("");
		setRequestParams((prevParams: any) => ({
			...prevParams,
			first: initialPage,
			rows: initialPageSize,
			page: initialPage,
			searchQuery: "",
		}));
		if (changeLoginForGetEmptyItemsBySearchQuery) {
			changeLoginForGetEmptyItemsBySearchQuery(false);
		}
		// setTimeout(() => {
		// 	setLoading(false);
		// }, 1000);
	};

	const renderHeader = () => {
		return (
			<div
				style={{
					display: "flex",
					// justifyContent:showGlobalSearch ? "space-between" : "end",
					justifyContent: "space-between",
					alignItems: "center",
				}}
			>
				{showGlobalSearch && (
					<span
						className="p-input-icon-left w-3"
						style={{ display: "flex", marginLeft: "10px", height: "37px" }}
					>
						<i className="pi pi-search" />
						<InputText
							value={itemSearch}
							onChange={(e) => setItemSearch(e.target.value)}
							placeholder={
								searchFieldPlaceholder ? searchFieldPlaceholder : "Search..."
							}
							className="w-full"
							style={{ borderRadius: "2px 0px 0px 2px" }}
						/>
						{itemSearch && (
							<Button
								icon="pi pi-times"
								onClick={handleClearSearch}
								className="p-button-warning"
								style={{ borderRadius: 0 }}
							/>
						)}
						<Button
							onClick={onSearchButtonClicked}
							severity={EnhancedButtonPrimeReactSeverity.Secondary}
							style={{
								// padding:"8px 10px 8px 10px",
								padding: "10px 22px 10px 10px",
								// marginRight:"-14px!important",
								borderRadius: "0px 2px 2px 0px",
								// backgroundColor: theme.palette.primary.main
							}}
						>
							{/* <SearchIcon /> */}
							Search
						</Button>
					</span>
				)}
				{/* {selectionToolbar && selectionToolbar(selectedItemsIds)} */}
				{selectionToolbar && selectionToolbar(selectedItems)}
			</div>
		);
	};
	const headerSearch = renderHeader();

	const onPage = (event: any) => {
		// // setRequestParams(event);
		// setLoading(true);
		// //imitate delay of a backend call
		// setTimeout(() => {
		// 	setRequestParams({
		// 		...event,
		// 		searchQuery: itemSearch,
		// 	});
		// 	setLoading(false);
		// }, 1000);
	};
	const [sortField, setSortField] = useState(null);
	const [sortOrder, setSortOrder] = useState(1);
	const onSort = useCallback(
		(event: any) => {
			// setLoading(true);

			let newSortOrder: any;
			if (event.sortField === sortField) {
				// Toggle sortOrder between 1 and -1
				newSortOrder = sortOrder === 1 ? -1 : 1;
				setSortOrder(newSortOrder);
			} else {
				// Set the new sortField and reset sortOrder to 1
				newSortOrder = 1;
				setSortField(event.sortField);
				setSortOrder(newSortOrder);
			}


			setRequestParams((prevParams: any) => ({
				...prevParams,
				sortOrder: newSortOrder !== 1 ? "asc" : "desc",
				sortField: event.sortField,
			}));

			// Imitate delay of a backend call
			// setTimeout(() => {
			// 	setLoading(false);
			// }, 1000);
		},
		[sortOrder, sortField]
	);
	// // This useEffect is triggered when the debouncedSearchQuery, pageSize or currentPage values change
	// useEffect(() => {
	// 	// if the results fetcher was passed, we can use it to fetch the results.
	// 	if (onRequestParamsChange) {
	// 		// skip the first initial call
	// 		if (isInitialRequest.current === true) {

	// 			isInitialRequest.current = false;
	// 		} else {

	// 			onRequestParamsChange(requestParams);
	// 		}
	// 	}
	// }, [onRequestParamsChange, requestParams, requestParamsToInitial]);

	useEffect(() => {
		if (requestParamsToInitial && requestParams.first !== initialPage) {
			setRequestParams({
				first: initialPage,
				rows: initialPageSize,
				page: initialPage,
				sortField: null,
				sortOrder: "asc",
				searchQuery: "",
				filters: {
					name: { value: "", matchMode: "contains" },
					"country.name": { value: "", matchMode: "contains" },
					company: { value: "", matchMode: "contains" },
					"representative.name": { value: "", matchMode: "contains" },
				},
			});

		}

	}, [requestParamsToInitial, onRequestParamsChange, requestParams]);

	const onSelectionModelChange = useCallback(
		(items: any) => {
			let selectedItemsIds = items.value.map((item: any) => item.id);
			// setSelectedItems(selectedItemsIds);
			// Check for deleted items and remove them from selectedItems
			const updatedSelectedItems = items.value.filter((selectedItem: any) => {
				return rows && rows.find((row: any) => row.id === selectedItem.id);
			});
			setSelectedItems(updatedSelectedItems);
			setSelectedItemsIds(selectedItemsIds);
			// this is for select all checkbox in footer bottom
			setManualSelectAll(
				!!(rows?.length && updatedSelectedItems.length === rows.length)
			);
			// if (selectedRowsValues) {
			// 	if (items.value.length > 0) {
			// 		const selectedRowData = rows.filter((row: any) => {
			// 			return items.value.includes(row);
			// 		});

			// 		selectedRowsValues(selectedRowData);
			// 	} else {
			// 		selectedRowsValues(items.value);

			// 	}
			// }
		},
		[
			rows,
			//  selectedRowsValues, 
			selectedItems]
	);
	// useEffect(() => {
	// 	// Check for deleted items and remove them from selectedItems
	// 	const updatedSelectedItems =
	// 		selectedItems &&
	// 		selectedItems.length > 0 &&
	// 		selectedItems.filter((selectedItem: any) => {
	// 			return rows && rows.find((row: any) => row.id === selectedItem.id);
	// 		});
	// 	// Only update if there are changes to selectedItems
	// 	if (
	// 		updatedSelectedItems &&
	// 		updatedSelectedItems.length !== selectedItems.length
	// 	) {
	// 		setSelectedItems(updatedSelectedItems);

	// 		// Update selectedItemsIds
	// 		const updatedSelectedItemsIds = updatedSelectedItems.map(
	// 			(item: any) => item.id
	// 		);
	// 		setSelectedItemsIds(updatedSelectedItemsIds);
	// 	}
	// }, [rows, selectedItems]);
	useEffect(() => {
		if (selectedItems && selectedItems.length > 0) {
			// Check if all selected items exist in rows
			const allExist = selectedItems.every((selectedItem: any) =>
				rows?.some((row: any) => row.employeeId === selectedItem.employeeId)
			);
			if (!allExist) {
				// ❌ One or more items removed → reset to initial state
				setSelectedItems([]);
				setSelectedItemsIds([]);
				// if (selectedRowsValues) selectedRowsValues([]);
			}
		}
	}, [rows]);

	const isInitialClearRender = useRef(true);
	useEffect(() => {
		if (isInitialClearRender.current) {
			isInitialClearRender.current = false;
			return;
		}
		setSelectedItems([]);
		setSelectedItemsIds([]);
		setManualSelectAll(false);
		// setActiveRowIndex(null)
		// if (selectedRowsValues) selectedRowsValues([]);
	}, [clearAllSelectedItemsForCalculatedPayrollTable]);


	// const onCellValueChange = (column: any, rowIndex: number, value: any) => {
	// 	if (showChecboxColumn) {
	// 		setPayrollItems((prev: any[]) => {
	// 			const updatedData = [...prev];
	// 			const updatedRow = { ...updatedData[rowIndex], [column.field]: value };
	// 			updatedData[rowIndex] = updatedRow;

	// 			// Fix: re-map selectedItems to updatedData references
	// 			setSelectedItems((prevSelected: any[]) =>
	// 				prevSelected
	// 					.map(sel => updatedData.find(r => r.id === sel.id))
	// 					.filter(Boolean)
	// 			);

	// 			return updatedData;
	// 		});
	// 	} else {
	// 		setPayrollItems((prev: any[]) => {
	// 			const updatedData = [...prev];
	// 			const updatedRow = updatedData[rowIndex];

	// 			// mutate only the needed field (keeps same object reference)
	// 			updatedRow[column.field] = value;

	// 			return updatedData;
	// 		});
	// 	}
	// };

	const onCellValueChange = useCallback(
		(column: any, rowIndex: number, value: any) => {
			setPayrollItems((prev: any[]) => {
				const updatedData = [...prev];
				updatedData[rowIndex] = { ...updatedData[rowIndex], [column.field]: value };
				return updatedData;
			});
		},
		[]
	);


	const editableNumberColumns = useMemo(
		() =>
			sortedColumns.filter(
				(col: any) =>
					col.isEditable &&
					col.type === "number"
			),
		[sortedColumns]
	);

	const editableNumberColumnsRef = useRef(editableNumberColumns);
	editableNumberColumnsRef.current = editableNumberColumns;
	const enableFrozenColumnsRef = useRef(enableFrozenColumns);
	enableFrozenColumnsRef.current = enableFrozenColumns;

	// const handleCellNavigation = useCallback(
	// 	(
	// 		e: React.KeyboardEvent<HTMLInputElement>,
	// 		rowIndex: number,
	// 		column: any
	// 	) => {
	// 		const key = e.key;

	// 		if (
	// 			key !== "ArrowUp" &&
	// 			key !== "ArrowDown" &&
	// 			key !== "ArrowLeft" &&
	// 			key !== "ArrowRight"
	// 		) {
	// 			return;
	// 		}

	// 		e.preventDefault();

	// 		const currentColumnIndex =
	// 			editableNumberColumnsRef.current.findIndex(
	// 				(col: any) => col.field === column.field
	// 			);

	// 		if (currentColumnIndex === -1) {
	// 			return;
	// 		}

	// 		let targetRowIndex = rowIndex;
	// 		let targetColumnIndex = currentColumnIndex;

	// 		if (key === "ArrowUp") {
	// 			targetRowIndex--;
	// 		} else if (key === "ArrowDown") {
	// 			targetRowIndex++;
	// 		} else if (key === "ArrowLeft") {
	// 			targetColumnIndex--;
	// 		} else if (key === "ArrowRight") {
	// 			targetColumnIndex++;
	// 		}

	// 		if (
	// 			targetRowIndex < 0 ||
	// 			targetRowIndex >= payrollItemsRef.current.length ||
	// 			targetColumnIndex < 0 ||
	// 			targetColumnIndex >= editableNumberColumnsRef.current.length
	// 		) {
	// 			return;
	// 		}

	// 		const targetColumn =
	// 			editableNumberColumnsRef.current[targetColumnIndex];

	// 		const targetRow =
	// 			payrollItemsRef.current[targetRowIndex];

	// 		if (!targetColumn || !targetRow) {
	// 			return;
	// 		}

	// 		const targetCellKey =
	// 			`${targetRow.employeeId}-${targetColumn.field}`;

	// 		const targetInput =
	// 			inputRefs.current[targetCellKey];

	// 		if (!targetInput) {
	// 			return;
	// 		}

	// 		targetInput.focus();

	// 		requestAnimationFrame(() => {
	// 			const inputValue = targetInput.value;

	// 			const decimalIndex =
	// 				inputValue.indexOf(".");

	// 			const cursorPosition =
	// 				decimalIndex >= 0
	// 					? decimalIndex
	// 					: inputValue.length;

	// 			targetInput.setSelectionRange(
	// 				cursorPosition,
	// 				cursorPosition
	// 			);
	// 		});
	// 	},
	// 	[]
	// );

	// FOR DIV TAG NUMBER CELL
	const handleCellNavigation = useCallback(
		(e: React.KeyboardEvent<any>, rowIndex: number, column: any) => {
			const key = e.key;
			if (key !== "ArrowUp" && key !== "ArrowDown" && key !== "ArrowLeft" && key !== "ArrowRight") return;
			e.preventDefault();

			const currentColumnIndex = editableNumberColumnsRef.current.findIndex(
				(col: any) => col.field === column.field
			);
			if (currentColumnIndex === -1) return;

			let targetRowIndex = rowIndex;
			let targetColumnIndex = currentColumnIndex;

			if (key === "ArrowUp") targetRowIndex--;
			else if (key === "ArrowDown") targetRowIndex++;
			else if (key === "ArrowLeft") targetColumnIndex--;
			else if (key === "ArrowRight") targetColumnIndex++;

			if (
				targetRowIndex < 0 ||
				targetRowIndex >= payrollItemsRef.current.length ||
				targetColumnIndex < 0 ||
				targetColumnIndex >= editableNumberColumnsRef.current.length
			) return;

			const targetColumn = editableNumberColumnsRef.current[targetColumnIndex];
			const targetRow = payrollItemsRef.current[targetRowIndex];
			if (!targetColumn || !targetRow) return;

			const targetCellKey = `${targetRow.employeeId}-${targetColumn.field}`;
			const targetEl = inputRefs.current[targetCellKey];
			if (!targetEl) return;

			targetEl.focus();

			requestAnimationFrame(() => {
				// CHANGED: branch by element type instead of assuming .value/.setSelectionRange
				if (targetEl instanceof HTMLInputElement) {
					const inputValue = targetEl.value;
					const decimalIndex = inputValue.indexOf(".");
					const cursorPosition = decimalIndex >= 0 ? decimalIndex : inputValue.length;
					targetEl.setSelectionRange(cursorPosition, cursorPosition);
				} else {
					placeCaretBeforeDecimal(targetEl);
				}
			});
		},
		[]
	);

	const getNumberFieldClass = (column: any) => {
		if (column.field === "vacationPay") {
			return "vacationPayField";
		}

		if (
			column.field === "holidayHours" ||
			column.field === "holidayPay" ||
			column.field === "holidayPayAccured"
		) {
			return "holidayHoursField";
		}

		if (column.edr === "earnings") {
			return "earningsField";
		}

		if (column.edr === "deductions") {
			return "deductionsField";
		}

		if (column.edr === "reimbursements") {
			return "reimbursementsField";
		}

		return "";
	};

	const getCellValue = (
		rowData: any,
		field: string
	) => {
		if (field.indexOf(".") === -1) {
			return rowData[field];
		}

		return getValueByFieldPath(
			rowData,
			field
		);
	};

	const getColumnClassName = (
		x: any
	) => {
		if (
			x.field === "vacationPay" ||
			x.field ===
			"vacationPayAccured"
		) {
			return "vacationPayField";
		}

		if (
			x.field === "holidayHours" ||
			x.field ===
			"holidayPayAccured" ||
			x.field === "holidayPay"
		) {
			return "holidayHoursField";
		}

		if (x.edr === "earnings") {
			return "earningsField";
		}

		if (x.edr === "deductions") {
			return "deductionsField";
		}

		if (
			x.edr === "reimbursements"
		) {
			return "reimbursementsField";
		}

		return "";
	};

	const columnMeta = useMemo(() => {
		return sortedColumns.map(
			(column: any) => ({
				...column,
				cellClassName:
					getColumnClassName(
						column
					),
				numberClassName:
					getNumberFieldClass(
						column
					),
			})
		);
	}, [sortedColumns]);

	const columnMetaMap = useMemo(() => {
		const map = new Map<string, any>();

		for (const column of columnMeta) {
			map.set(column.field, column);
		}

		return map;
	}, [columnMeta]);
	const handleGridFocus = useCallback(
		(event: React.FocusEvent<HTMLDivElement>) => {
			const target = event.target as HTMLInputElement;

			if (!target.dataset.payrollInput) {
				return;
			}

			const employeeId = target.dataset.employeeId;

			if (!employeeId) return;

			const container = containerRef.current;

			if (!container) return;

			// Remove previous row highlight
			container
				.querySelectorAll(".highlighted-row")
				.forEach((row) => {
					row.classList.remove("highlighted-row");
				});

			// Find all inputs belonging to this employee
			container
				.querySelectorAll(
					`input[data-payroll-input="true"][data-employee-id="${employeeId}"]`
				)
				.forEach((input) => {
					const row = input.closest("tr");

					if (row) {
						row.classList.add("highlighted-row");
					}
				});
		},
		[]
	);

	// const handleGridBlur = useCallback(
	// 	(event: React.FocusEvent<HTMLDivElement>) => {
	// 		const target = event.target as HTMLInputElement;

	// 		if (!target.dataset.payrollInput) {
	// 			return;
	// 		}

	// 		const rowIndex = Number(target.dataset.rowIndex);
	// 		const field = target.dataset.field;

	// 		if (!field) {
	// 			return;
	// 		}

	// 		const raw = target.value.trim();

	// 		// Empty input
	// 		if (raw === "") {
	// 			target.value = "";

	// 			const column = columnMeta.find(
	// 				(item: any) => item.field === field
	// 			);

	// 			if (!column) return;

	// 			onCellValueChange(
	// 				column,
	// 				rowIndex,
	// 				null,
	// 				// payrollItems[rowIndex]
	// 			);

	// 			return;
	// 		}

	// 		const numericValue = Number(
	// 			raw.replace(/,/g, "")
	// 		);

	// 		if (Number.isNaN(numericValue)) {
	// 			return;
	// 		}

	// 		// minFractionDigits={2}
	// 		// maxFractionDigits={2}
	// 		const formattedValue = numericValue.toFixed(2);

	// 		// IMPORTANT:
	// 		// update native input without React re-render
	// 		target.value = formattedValue;

	// 		const column = columnMeta.find(
	// 			(item: any) => item.field === field
	// 		);

	// 		if (!column) {
	// 			return;
	// 		}

	// 		onCellValueChange(
	// 			column,
	// 			rowIndex,
	// 			numericValue,
	// 			// payrollItems[rowIndex]
	// 		);
	// 	},
	// 	[columnMeta, payrollItems, onCellValueChange]
	// );

	const handleGridBlur = useCallback(
		(event: React.FocusEvent<HTMLDivElement>) => {
			const target = event.target as HTMLElement;

			if (!target.dataset.payrollInput) return;

			// Native <input type="date"> commits through its own onChange — not through blur here.
			if (!target.isContentEditable) return;

			const rowIndex = Number(target.dataset.rowIndex);
			const field = target.dataset.field;
			const cellType = target.dataset.cellType; // "number" | "text"

			if (!field || Number.isNaN(rowIndex)) return;

			const column = columnMeta.find((item: any) => item.field === field);
			if (!column) return;

			const raw = (target.textContent ?? "").trim();

			if (cellType === "text") {
				onCellValueChange(column, rowIndex, raw === "" ? null : raw);
				return;
			}

			// numeric path
			if (raw === "") {
				target.textContent = "";
				onCellValueChange(column, rowIndex, null);
				return;
			}

			const numericValue = Number(raw.replace(/,/g, ""));
			if (Number.isNaN(numericValue)) return;

			// const formattedValue = numericValue.toFixed(2);
			const formattedValue =
				formatAmountCommaSeperator(numericValue);
			target.textContent = formattedValue;
			onCellValueChange(column, rowIndex, numericValue);
		},
		[columnMeta, onCellValueChange]
	);

	const handleGridKeyDown = useCallback(
		(event: React.KeyboardEvent<HTMLDivElement>) => {
			const target =
				event.target as HTMLInputElement;

			if (!target.dataset.payrollInput) {
				return;
			}

			const rowIndex =
				Number(target.dataset.rowIndex);

			const field =
				target.dataset.field;

			if (!field || Number.isNaN(rowIndex)) {
				return;
			}

			const column =
				editableNumberColumnsRef.current.find(
					(item: any) =>
						item.field === field
				);

			if (!column) {
				return;
			}

			handleCellNavigation(
				event as unknown as React.KeyboardEvent<HTMLInputElement>,
				rowIndex,
				column
			);
		},
		[handleCellNavigation]
	);

	const handleGridClick = useCallback(
		(event: React.MouseEvent<HTMLDivElement>) => {
			const target = event.target as HTMLElement;
			const row = target.closest('tr');
			if (!row) return;

			const tbody = row.closest('.p-datatable-tbody');
			if (!tbody) return;

			const container = containerRef.current;
			if (!container) return;

			container
				.querySelectorAll('.highlighted-row')
				.forEach((el) => el.classList.remove('highlighted-row'));

			row.classList.add('highlighted-row');
		},
		[]
	);
	const inputRefCallbacks = useRef(
		new Map<string, (element: HTMLInputElement | null) => void>()
	);

	// const getInputRefCallback = useCallback(
	// 	(cellKey: string) => {
	// 		let callback =
	// 			inputRefCallbacks.current.get(cellKey);

	// 		if (!callback) {
	// 			callback = (element) => {
	// 				if (element) {
	// 					inputRefs.current[cellKey] = element;
	// 				} else {
	// 					delete inputRefs.current[cellKey];
	// 				}
	// 			};

	// 			inputRefCallbacks.current.set(
	// 				cellKey,
	// 				callback
	// 			);
	// 		}

	// 		return callback;
	// 	},
	// 	[]
	// );

	const getInputRefCallback = useCallback(
		(cellKey: string) => {
			let callback = inputRefCallbacks.current.get(cellKey);

			if (!callback) {
				callback = (element: HTMLElement | null) => {
					if (element) {
						inputRefs.current[cellKey] = element;
					} else {
						delete inputRefs.current[cellKey];
					}
				};

				inputRefCallbacks.current.set(cellKey, callback);
			}

			return callback;
		},
		[]
	);


	// const renderNumberInput = (
	// 	rowData: any,
	// 	rowIndex: number,
	// 	column: any
	// ) => {
	// 	const value =
	// 		getCellValue(rowData, column.field);

	// 	const displayValue =
	// 		value === null || value === undefined || value === ""
	// 			? ""
	// 			: Number(String(value).replace(/,/g, "")).toFixed(2);


	// 	const cellKey =
	// 		`${rowData.employeeId}-${column.field}`;

	// 	return (
	// 		<input
	// 			key={`${rowData.employeeId}-${column.field}-${displayValue}`}
	// 			ref={getInputRefCallback(`${rowData.employeeId}-${column.field}`)}
	// 			data-payroll-input="true"
	// 			data-row-index={rowIndex}
	// 			data-field={column.field}
	// 			data-employee-id={rowData.employeeId}
	// 			type="text"
	// 			inputMode="decimal"
	// 			defaultValue={displayValue}
	// 			className={`payroll-number-input ${column.numberClassName || ""}`}


	// 			onInput={(e) => {
	// 				const input = e.currentTarget;

	// 				let value = input.value;

	// 				// Only numbers, decimal and minus
	// 				value = value.replace(/[^\d.-]/g, "");

	// 				// Only one decimal point
	// 				const parts = value.split(".");

	// 				if (parts.length > 2) {
	// 					value = `${parts[0]}.${parts.slice(1).join("")}`;
	// 				}

	// 				// Maximum 2 decimal places
	// 				const decimalParts = value.split(".");

	// 				if (decimalParts.length === 2) {
	// 					value = `${decimalParts[0]}.${decimalParts[1].slice(0, 2)}`;
	// 				}

	// 				input.value = value;
	// 			}}
	// 		/>
	// 	);
	// };

	// const renderTextInput = (
	// 	rowData: any,
	// 	rowIndex: number,
	// 	column: any
	// ) => {
	// 	const value = getCellValue(rowData, column.field) ?? "";

	// 	const inputKey =
	// 		`${rowData.employeeId}-${column.field}`;

	// 	return (
	// 		<input
	// 			key={inputKey}
	// 			ref={getInputRefCallback(inputKey)}
	// 			data-payroll-input="true"
	// 			data-row-index={rowIndex}
	// 			data-field={column.field}
	// 			data-employee-id={rowData.employeeId}
	// 			type="text"
	// 			defaultValue={value}
	// 			className="payroll-text-input"
	// 			onInput={(e) => {
	// 				onCellValueChange(
	// 					column,
	// 					rowIndex,
	// 					e.currentTarget.value
	// 				);
	// 			}}
	// 		/>
	// 	);
	// };
	// const renderCalendarInput = (
	// 	rowData: any,
	// 	rowIndex: number,
	// 	column: any
	// ) => {
	// 	const value = getCellValue(rowData, column.field);

	// 	const formattedValue = value
	// 		? new Date(value).toISOString().split("T")[0]
	// 		: "";

	// 	return (
	// 		<input
	// 			key={`${rowData.employeeId}-${column.field}-${formattedValue}`}
	// 			ref={getInputRefCallback(
	// 				`${rowData.employeeId}-${column.field}`
	// 			)}
	// 			data-payroll-input="true"
	// 			data-row-index={rowIndex}
	// 			data-field={column.field}
	// 			data-employee-id={rowData.employeeId}
	// 			type="date"
	// 			defaultValue={formattedValue}
	// 			className="payroll-date-input"
	// 			onClick={(e) => {
	// 				const input = e.currentTarget;

	// 				if (typeof input.showPicker === "function") {
	// 					input.showPicker();
	// 				}
	// 			}}
	// 			onChange={(e) => {
	// 				const newValue = e.currentTarget.value;

	// 				onCellValueChange(
	// 					column,
	// 					rowIndex,
	// 					newValue
	// 						? new Date(`${newValue}T00:00:00`)
	// 						: null
	// 				);
	// 			}}
	// 		/>
	// 	);
	// };

	// const renderCalendarInput = (
	// 	rowData: any,
	// 	rowIndex: number,
	// 	column: any
	// ) => {
	// 	const value = getCellValue(rowData, column.field);

	// 	const inputKey = `${rowData.employeeId}-${column.field}`;

	// 	const dateValue = value
	// 		? formatDateToShow(value)
	// 		: "";

	// 	return (
	// 		<input
	// 			key={inputKey}
	// 			ref={getInputRefCallback(inputKey)}
	// 			data-payroll-input="true"
	// 			data-row-index={rowIndex}
	// 			data-field={column.field}
	// 			data-employee-id={rowData.employeeId}
	// 			type="date"
	// 			defaultValue={dateValue}
	// 			className="payroll-date-input"
	// 			onClick={(e) => {
	// 				const input = e.currentTarget;

	// 				// Open native calendar directly
	// 				if (input.showPicker) {
	// 					try {
	// 						input.showPicker();
	// 					} catch {
	// 						// Browser may already have opened it.
	// 					}
	// 				}
	// 			}}
	// 			onChange={(e) => {
	// 				const value = e.currentTarget.value;

	// 				if (!value) {
	// 					onCellValueChange(
	// 						column,
	// 						rowIndex,
	// 						null
	// 					);

	// 					return;
	// 				}

	// 				const [year, month, day] =
	// 					value.split("-").map(Number);

	// 				const selectedDate = new Date(
	// 					year,
	// 					month - 1,
	// 					day
	// 				);

	// 				onCellValueChange(
	// 					column,
	// 					rowIndex,
	// 					selectedDate
	// 				);
	// 			}}
	// 		/>
	// 	);
	// };

	const toIsoDateInputValue = (value: any) => {
		if (!value) return "";
		const d = new Date(value);
		if (isNaN(d.getTime())) return "";
		const yyyy = d.getFullYear();
		const mm = String(d.getMonth() + 1).padStart(2, "0");
		const dd = String(d.getDate()).padStart(2, "0");
		return `${yyyy}-${mm}-${dd}`;
	};

	// const renderCalendarInput = (
	// 	rowData: any,
	// 	rowIndex: number,
	// 	column: any
	// ) => {
	// 	const value = getCellValue(rowData, column.field);

	// 	const inputKey = `${rowData.employeeId}-${column.field}`;

	// 	// Real <input type="date"> needs strict ISO yyyy-MM-dd
	// 	const isoValue = toIsoDateInputValue(value);

	// 	// What the user actually sees, styled like the old PrimeReact Calendar ("M dd, yy")
	// 	const displayValue = value ? formatDateToShow(value) : "";

	// 	return (
	// 		<span className="payroll-date-input-wrapper">
	// 			<input
	// 				key={inputKey}
	// 				ref={getInputRefCallback(inputKey)}
	// 				data-payroll-input="true"
	// 				data-row-index={rowIndex}
	// 				data-field={column.field}
	// 				data-employee-id={rowData.employeeId}
	// 				type="date"
	// 				defaultValue={isoValue}
	// 				className="payroll-date-input"
	// 				onClick={(e) => {
	// 					const input = e.currentTarget;
	// 					if (input.showPicker) {
	// 						try {
	// 							input.showPicker();
	// 						} catch {
	// 							// Browser may already have it open.
	// 						}
	// 					}
	// 				}}
	// 				onChange={(e) => {
	// 					const raw = e.currentTarget.value;

	// 					const wrapper = e.currentTarget.closest(
	// 						".payroll-date-input-wrapper"
	// 					);
	// 					const label = wrapper?.querySelector(
	// 						".payroll-date-display"
	// 					) as HTMLElement | null;

	// 					if (!raw) {
	// 						if (label) label.textContent = "";
	// 						onCellValueChange(column, rowIndex, null);
	// 						return;
	// 					}

	// 					const [year, month, day] = raw.split("-").map(Number);
	// 					const selectedDate = new Date(year, month - 1, day);

	// 					if (label) {
	// 						label.textContent = formatDateToShow(selectedDate);
	// 					}

	// 					onCellValueChange(column, rowIndex, selectedDate);
	// 				}}
	// 			/>
	// 			<span className="payroll-date-display">{displayValue}</span>
	// 		</span>
	// 	);
	// };

	const formatNumericDisplayValue = useCallback((value: any) => {
		if (value === null || value === undefined || value === "") return "";
		const numeric = typeof value === "string" ? Number(value.replace(/,/g, "")) : value;
		if (typeof numeric !== "number" || Number.isNaN(numeric)) return "";
		return formatAmountCommaSeperator(numeric);
	}, []);
	const columnsBody = useCallback((rowData: any, row: any, x: any) => {

		const isPreCalcEditableField =
			x.field === "cpp" ||
			x.field === "ei" ||
			x.field === "qpip" ||
			x.field === "federalTax" ||
			x.field === "provincialTax";

		const periodNotYetProcessed =
			selectedPayPeriod?.process === null || selectedPayPeriod?.process === false;

		// And if we are using column body only then we have to use this
		if (
			// x.isEditable &&
			// (selectedPayPeriod?.process === null || selectedPayPeriod?.process === false) &&
			// (
			// 	x.field === "cpp" ||
			// 	x.field === "ei" ||
			// 	x.field === "qpip" ||
			// 	x.field === "federalTax" ||
			// 	x.field === "provincialTax"
			// )

			x.isEditable && isPreCalcEditableField && periodNotYetProcessed
		) {
			// if (x.type === 'number') {
			// 	// return (
			// 	// 	<InputNumber
			// 	// 		value={rowData[x.field]}
			// 	// 		// onChange={(e: any) => onCellValueChange(x, row.rowIndex, e.value)}
			// 	// 		onValueChange={(e: any) => onCellValueChange(x, row.rowIndex, e.value)}
			// 	// 		// placeholder="0.00"
			// 	// 		minFractionDigits={2}
			// 	// 		maxFractionDigits={2}
			// 	// 		// inputClassName={`${x.field === 'vacationPay' ? "vacationPayField" : ""}`}
			// 	// 		inputClassName={
			// 	// 			x.field === 'vacationPay' ? 'vacationPayField' :
			// 	// 				x.field === 'holidayHours' ? 'holidayHoursField' :
			// 	// 					x.field === 'holidayPay' ? 'holidayHoursField' :
			// 	// 						x.field === 'holidayPayAccured' ? 'holidayHoursField' :
			// 	// 							x.edr === 'earnings' ? 'earningsField' :
			// 	// 								x.edr === 'deductions' ? 'deductionsField' :
			// 	// 									x.edr === 'reimbursements' ? 'reimbursementsField' :
			// 	// 										''}
			// 	// 		inputStyle={{ textAlign: "right" }}
			// 	// 		// onFocus={() => setActiveRowIndex(row.rowIndex)}
			// 	// 		onFocus={() => setActiveRowIndex(rowData.employeeId)}
			// 	// 	/>
			// 	// )

			// 	// return renderNumberInput(
			// 	// 	rowData,
			// 	// 	row.rowIndex,
			// 	// 	x
			// 	// );

			// 	const value = getCellValue(
			// 		rowData,
			// 		x.field
			// 	);
			// 	// const displayValue =
			// 	// 	value === null || value === undefined || value === ""
			// 	// 		? ""
			// 	// 		// : Number(String(value).replace(/,/g, "")).toFixed(2);
			// 	// 		: formatAmountCommaSeperator(value)
			// 	const displayValue = value === null || value === undefined ? "" : formatAmountCommaSeperator(value);
			// 	const cellKey = `${rowData.employeeId}-${x.field}`;
			// 	return (
			// 		// <NumberCell
			// 		// 	key={cellKey}
			// 		// 	rowIndex={row.rowIndex}
			// 		// 	field={x.field}
			// 		// 	employeeId={rowData.employeeId}
			// 		// 	displayValue={displayValue}
			// 		// 	className={`payroll-number-input ${getNumberFieldClass(x) || ""}`}
			// 		// 	registerRef={getInputRefCallback(cellKey)}
			// 		// 	column={x}                          // CHANGED
			// 		// 	onCellKeyDown={handleCellNavigation} // CHANGED
			// 		// />

			// 		<EditableCell
			// 			key={cellKey}
			// 			cellType="number"
			// 			rowIndex={row.rowIndex}
			// 			field={x.field}
			// 			employeeId={rowData.employeeId}
			// 			displayValue={displayValue}
			// 			className={`payroll-number-input ${getNumberFieldClass(x) || ""}`}
			// 			registerRef={getInputRefCallback(cellKey)}
			// 			column={x}
			// 			onCellKeyDown={handleCellNavigation}
			// 		/>
			// 	);
			// }

			const value = getCellValue(rowData, x.field);
			const displayValue = formatNumericDisplayValue(value);
			const cellKey = `${rowData.employeeId}-${x.field}`;
			return (
				<EditableCell
					key={cellKey}
					cellType="number"
					rowIndex={row.rowIndex}
					field={x.field}
					employeeId={rowData.employeeId}
					displayValue={displayValue}
					className={`payroll-number-input ${getNumberFieldClass(x) || ""}`}
					registerRef={getInputRefCallback(cellKey)}
					column={x}
					onCellKeyDown={handleCellNavigation}
				/>
			);
		} else if (
			x.isEditable &&
			(selectedPayPeriod?.process !== null || selectedPayPeriod?.process !== false) &&
			(
				x.field !== "employeeName" ||
				x.field !== "employeeNumberId" ||
				x.field !== "employeeId" ||
				x.field !== "checkReferanceNumber" ||
				x.field !== "checkDate" ||
				x.field !== "cpp" ||
				x.field !== "ei" ||
				x.field !== "qpip" ||
				x.field !== "federalTax" ||
				x.field !== "provincialTax"
			)
		) {
			if (x.type === 'number') {
				return (
					<span
						className="payroll-cell-text"
					// onClick={() => setActiveRowIndex(rowData.employeeId)}
					>
						{formatAmountCommaSeperator(rowData[x.field])}
					</span>
				)
			}
		} else if (x.field === 'checkReferanceNumber' && rowData.payMethod === 300) {
			return (
				<span
					style={{ fontSize: ".875rem", marginLeft: "2.5rem", paddingTop: "4px", whiteSpace: "nowrap" }}
					// onClick={() => setActiveRowIndex(rowData.employeeId)}
					className="payroll-cell-text directDepositMessage"
				>
					Direct Deposit&nbsp;
					{!rowData.haveBank && <Tooltip title={"Payment method is selected as direct deposit, bank details are missing"} placement={"top"}>
						<InfoIcon
							sx={{
								fontSize: "1.1rem",
								margin: "5px 4px 4px 4px",
								cursor: "pointer",
							}}
						/>
					</Tooltip>}
				</span>
			)
		} else if (x.field === 'checkReferanceNumber' && rowData.payMethod !== 300) {
			// return (
			// 	<InputText
			// 		value={rowData[x.field]}
			// 		onChange={(e: any) => onCellValueChange(x, row.rowIndex, e.target.value)}
			// 	// onFocus={() => setActiveRowIndex(rowData.employeeId)}
			// 	/>
			// )
			// return renderTextInput(
			// 	rowData,
			// 	row.rowIndex,
			// 	x
			// );

			const value = getCellValue(rowData, x.field);
			const displayValue = value === null || value === undefined ? "" : String(value);
			const cellKey = `${rowData.employeeId}-${x.field}`;
			return (
				<EditableCell
					key={cellKey}
					cellType="text"
					rowIndex={row.rowIndex}
					field={x.field}
					employeeId={rowData.employeeId}
					displayValue={displayValue}
					className="payroll-text-input"
					registerRef={getInputRefCallback(cellKey)}
					column={x}
					onCellKeyDown={handleCellNavigation}
				/>
			);
		} else if (x.field === 'checkDate' && rowData.payMethod !== 300) {
			return (
				<Calendar
					value={rowData[x.field]}
					style={{
						margin: "8px 0px 4px 0px",
						width: "auto",
					}}
					onChange={(e: any) => onCellValueChange(x, row.rowIndex, e.value)}
					dateFormat="M dd, yy"
					placeholder={"dd/mm/yyyy"}
					showButtonBar
				// onFocus={() => setActiveRowIndex(rowData.employeeId)}
				/>
			)
			// return renderCalendarInput(
			// 	rowData,
			// 	row.rowIndex,
			// 	x
			// );

			const value = getCellValue(rowData, x.field);
			const isoValue = toIsoDateInputValue(value);
			const displayValue = value ? formatDateToShow(value) : "";
			const cellKey = `${rowData.employeeId}-${x.field}`;
			// return (
			// 	// <DateCell
			// 	// 	key={cellKey}
			// 	// 	rowIndex={row.rowIndex}
			// 	// 	field={x.field}
			// 	// 	employeeId={rowData.employeeId}
			// 	// 	isoValue={isoValue}
			// 	// 	displayValue={displayValue}
			// 	// 	registerRef={getInputRefCallback(cellKey)}
			// 	// 	onChangeDate={(rIdx, date) => onCellValueChange(x, rIdx, date)}
			// 	// />

			// 	<DateCell
			// 		key={cellKey}
			// 		rowIndex={row.rowIndex}
			// 		field={x.field}
			// 		employeeId={rowData.employeeId}
			// 		displayValue={displayValue}
			// 		registerRef={getInputRefCallback(cellKey)}
			// 		onChangeDate={(rIdx, date) => onCellValueChange(x, rIdx, date)}
			// 		onCellKeyDown={handleCellNavigation}
			// 		column={x}
			// 	/>
			// );
		}
		else {

			return (
				<span style={{ fontSize: ".875rem" }}
				// onClick={() => setActiveRowIndex(rowData.employeeId)}
				>
					{rowData[x.field]}
				</span>
				// <span onClick={() => setActiveRowIndex(rowData.employeeId)}>
				// 	{rowData[x.field]}
				// </span>
			)
		}
	},
		[
			onCellValueChange,
			handleCellNavigation,
			selectedPayPeriod?.process
			// setActiveRowIndex,
		]
	);



	// const containerRef = useRef<HTMLDivElement>(null);
	// const [containerWidth, setContainerWidth] = useState(0);
	// const [totalColumnWidth, setTotalColumnWidth] = useState(0);

	// useEffect(() => {
	// 	if (!containerRef.current) return;
	// 	const observer = new ResizeObserver(([entry]) => {
	// 		setContainerWidth(entry.contentRect.width);
	// 	});
	// 	observer.observe(containerRef.current);
	// 	return () => observer.disconnect();
	// }, []);
	// useEffect(() => {
	// 	// compute total defined width of all columns
	// 	const totalColumnWidth = columns && columns.reduce(
	// 		(sum: any, col: any) => sum + (col.maxWidth || 0),
	// 		0
	// 	);
	// 	setTotalColumnWidth(totalColumnWidth)
	// }, [columns]);




	// // row highlighting (yellow background) when clicking any cell in that row
	// const [activeRowIndex, setActiveRowIndex] = useState<number | null>(null);


	// const getRowClassName = (data: any, options: any) => {

	// 	return data.employeeId === activeRowIndex ? "highlighted-row" : "";
	// };


	const containerRef = useRef<HTMLDivElement>(null);
	const [containerWidth, setContainerWidth] = useState(0);

	const totalColumnWidth = useMemo(() => {
		return columns?.reduce(
			(sum: number, col: any) => sum + (Number(col.maxWidth) || 0),
			0
		) ?? 0;
	}, [columns]);

	const columnCount = columns?.length ?? 0;
	const useFitContainerLogic = columnCount <= 10;

	useLayoutEffect(() => {
		// Skip the measurement entirely for wide grids — this is the case
		// that was costing the extra render/layout pass.
		if (!useFitContainerLogic) return;

		const updateWidth = () => {
			const width = containerRef.current?.clientWidth ?? 0;

			setContainerWidth((prev) =>
				prev === width ? prev : width
			);
		};

		requestAnimationFrame(updateWidth);

		window.addEventListener("resize", updateWidth);

		return () => {
			window.removeEventListener("resize", updateWidth);
		};
	}, [useFitContainerLogic]);

	const shouldFitContainer =
		useFitContainerLogic && totalColumnWidth < containerWidth;

	const columnsKey = useMemo(
		() =>
			columns
				?.map((column: any) =>
					`${column.field}:${column.hide}:${column.maxWidth}`
				)
				.join("|"),
		[columns]
	);

	// ─── STICKY HEADER USEEFFECT ───────────────────────────────────────────────
	// useEffect(() => {
	// 	const NAVBAR_HEIGHT = 112;
	// 	let stickyTable: HTMLElement | null = null;

	// 	const syncHorizontalScroll = () => {
	// 		if (!stickyTable || !containerRef.current) return;
	// 		const wrapper = containerRef.current.querySelector('.p-datatable-wrapper') as HTMLElement;
	// 		const clipWrapper = stickyTable.querySelector('[data-clip-wrapper]') as HTMLElement;
	// 		const cloneTable = stickyTable.querySelector('table') as HTMLElement;
	// 		if (!wrapper || !cloneTable || !clipWrapper) return;

	// 		const scrollLeft = wrapper.scrollLeft;

	// 		// Move the clip wrapper left (clips non-frozen columns at frozen boundary)
	// 		// Frozen columns are OUTSIDE this wrapper so they never move
	// 		cloneTable.style.transform = `translateX(-${scrollLeft}px)`;

	// 		const cloneThs = cloneTable.querySelectorAll('th') as NodeListOf<HTMLElement>;
	// 		const originalThs = containerRef.current.querySelectorAll('.p-datatable-thead th') as NodeListOf<HTMLElement>;

	// 		cloneThs.forEach((cloneTh, i) => {
	// 			const originalTh = originalThs[i] as HTMLElement;
	// 			if (!originalTh) return;

	// 			const isFrozen =
	// 				originalTh.classList.contains('p-frozen-column') ||
	// 				originalTh.classList.contains('selectionHeader');

	// 			if (isFrozen) {
	// 				// Counter-translate frozen columns — pin them back
	// 				cloneTh.style.transform = `translateX(${scrollLeft}px)`;
	// 				cloneTh.style.borderLeft = '1px solid #e5e7eb';
	// 				// cloneTh.style.borderRight = '1px solid #e5e7eb';
	// 				cloneTh.style.borderTop = '1px solid #e5e7eb';
	// 				cloneTh.style.borderBottom = '2px solid #e5e7eb';
	// 				cloneTh.style.backgroundColor = '#ffffff';
	// 				cloneTh.style.zIndex = '20';
	// 				cloneTh.style.position = 'sticky';
	// 			} else {
	// 				cloneTh.style.transform = '';
	// 				cloneTh.style.borderLeft = '1px solid #e5e7eb';
	// 				// cloneTh.style.borderRight = '1px solid #e5e7eb';
	// 				cloneTh.style.borderTop = '1px solid #e5e7eb';
	// 				cloneTh.style.borderBottom = '2px solid #e5e7eb';
	// 			}
	// 		});
	// 	};

	// 	const createStickyHeader = () => {
	// 		if (!containerRef.current) return;
	// 		const originalTable = containerRef.current.querySelector('.p-datatable-table') as HTMLElement;
	// 		const originalThead = containerRef.current.querySelector('.p-datatable-thead') as HTMLElement;
	// 		const wrapper = containerRef.current.querySelector('.p-datatable-wrapper') as HTMLElement;
	// 		if (!originalTable || !originalThead || !wrapper) return;

	// 		document.querySelector('[data-sticky-table]')?.remove();

	// 		const containerRect = containerRef.current.getBoundingClientRect();

	// 		const fixedContainer = document.createElement('div');
	// 		fixedContainer.setAttribute('data-sticky-table', 'true');
	// 		fixedContainer.style.cssText = `
	//         position: fixed;
	//         top: ${NAVBAR_HEIGHT}px;
	//         left: ${containerRect.left}px;
	//         width: ${containerRect.width}px;
	//         overflow: hidden;
	//         z-index: 999;
	//         background: #ffffff;
	//     `;

	// 		// const cloneTable = originalTable.cloneNode(true) as HTMLElement;

	// 		const cloneTable =
	// 			document.createElement("table");

	// 		cloneTable.className =
	// 			"p-datatable-table";

	// 		const clonedThead =
	// 			originalThead.cloneNode(
	// 				true
	// 			) as HTMLElement;

	// 		cloneTable.appendChild(
	// 			clonedThead
	// 		);

	// 		// ── KEY FIX: use separate borders instead of border-collapse ──────────
	// 		// border-collapse causes shared borders that break when translateX is applied
	// 		cloneTable.style.cssText = `
	//         table-layout: fixed;
	//         width: ${originalTable.offsetWidth}px;
	//         margin: 0;
	//         border-collapse: separate;
	//         border-spacing: 0;
	//         transform: translateX(-${wrapper.scrollLeft}px);
	//     `;

	// 		cloneTable.querySelector('.p-datatable-tbody')?.remove();
	// 		cloneTable.querySelector('.p-datatable-tfoot')?.remove();

	// 		const originalThs = originalThead.querySelectorAll('th') as NodeListOf<HTMLElement>;
	// 		const cloneThs = cloneTable.querySelectorAll('th') as NodeListOf<HTMLElement>;

	// 		// Calculate frozen columns total width for clip wrapper offset
	// 		let frozenWidth = 0;

	// 		originalThs.forEach((th, i) => {
	// 			const cloneTh = cloneThs[i] as HTMLElement;
	// 			if (!cloneTh) return;

	// 			const width = th.getBoundingClientRect().width;
	// 			const computedStyle = window.getComputedStyle(th);
	// 			const computedBg = computedStyle.backgroundColor;
	// 			const isFrozen =
	// 				th.classList.contains('p-frozen-column') ||
	// 				th.classList.contains('selectionHeader');

	// 			if (isFrozen) frozenWidth += width;

	// 			cloneTh.style.width = `${width}px`;
	// 			cloneTh.style.minWidth = `${width}px`;
	// 			cloneTh.style.maxWidth = `${width}px`;
	// 			// ── Every th gets ALL 4 borders explicitly ────────────────────────
	// 			cloneTh.style.borderTop = '1px solid #e5e7eb';
	// 			cloneTh.style.borderBottom = '2px solid #e5e7eb';
	// 			// cloneTh.style.borderRight = '1px solid #e5e7eb';
	// 			cloneTh.style.borderLeft = '1px solid #e5e7eb';  // ← ALL columns get left border
	// 			cloneTh.style.boxSizing = 'border-box';
	// 			cloneTh.style.backgroundColor = (computedBg && computedBg !== 'rgba(0, 0, 0, 0)') ? computedBg : '#ffffff';
	// 			cloneTh.style.color = computedStyle.color || '#0091b3';
	// 			cloneTh.style.fontWeight = 'bold';
	// 			cloneTh.style.fontSize = computedStyle.fontSize || '0.9rem';
	// 			cloneTh.style.fontFamily = computedStyle.fontFamily;
	// 			cloneTh.style.textAlign = 'left';
	// 			cloneTh.style.padding = '6px';
	// 			cloneTh.style.whiteSpace = 'nowrap';
	// 			cloneTh.style.overflow = 'hidden';
	// 			cloneTh.style.textOverflow = 'ellipsis';
	// 			cloneTh.style.verticalAlign = 'middle';

	// 			if (isFrozen) {
	// 				cloneTh.style.position = 'sticky';
	// 				cloneTh.style.left = th.style.left || '0px';
	// 				cloneTh.style.zIndex = '20';
	// 				cloneTh.style.backgroundColor = '#ffffff';
	// 				// Shadow on RIGHT side of last frozen column only
	// 			} else {
	// 				cloneTh.style.position = '';
	// 				cloneTh.style.left = '';
	// 				cloneTh.style.zIndex = '1';
	// 			}

	// 			const headerContent = cloneTh.querySelector('.p-column-header-content') as HTMLElement;
	// 			if (headerContent) {
	// 				headerContent.style.display = 'flex';
	// 				headerContent.style.alignItems = 'center';
	// 				headerContent.style.color = computedStyle.color || '#0091b3';
	// 				headerContent.style.fontWeight = 'bold';
	// 			}

	// 			const titleSpan = cloneTh.querySelector('.p-column-title') as HTMLElement;
	// 			if (titleSpan) {
	// 				titleSpan.style.color = computedStyle.color || '#0091b3';
	// 				titleSpan.style.fontWeight = 'bold';
	// 				titleSpan.style.fontSize = computedStyle.fontSize || '0.9rem';
	// 				titleSpan.style.whiteSpace = 'nowrap';
	// 			}
	// 		});

	// 		// ── Selection column ──────────────────────────────────────────────────
	// 		const cloneSelectionTh = cloneTable.querySelector('.selectionHeader') as HTMLElement;
	// 		if (cloneSelectionTh) {
	// 			cloneSelectionTh.style.width = '35px';
	// 			cloneSelectionTh.style.minWidth = '35px';
	// 			cloneSelectionTh.style.maxWidth = '35px';
	// 			cloneSelectionTh.style.textAlign = 'center';
	// 			cloneSelectionTh.style.padding = '0';
	// 			cloneSelectionTh.style.position = 'sticky';
	// 			cloneSelectionTh.style.left = '0px';
	// 			cloneSelectionTh.style.zIndex = '20';
	// 			cloneSelectionTh.style.backgroundColor = '#ffffff';
	// 			cloneSelectionTh.style.borderLeft = '1px solid #e5e7eb';
	// 			// cloneSelectionTh.style.borderRight = '1px solid #e5e7eb';
	// 			cloneSelectionTh.style.borderTop = '1px solid #e5e7eb';
	// 			cloneSelectionTh.style.borderBottom = '2px solid #e5e7eb';

	// 			const headerContent = cloneSelectionTh.querySelector('.p-column-header-content') as HTMLElement;
	// 			if (headerContent) {
	// 				headerContent.style.display = 'flex';
	// 				headerContent.style.justifyContent = 'center';
	// 				headerContent.style.alignItems = 'center';
	// 				headerContent.style.width = '100%';
	// 				headerContent.style.height = '100%';
	// 				headerContent.style.padding = '0';
	// 			}

	// 			cloneSelectionTh.querySelectorAll('*').forEach((el: any) => {
	// 				el.style.pointerEvents = 'none';
	// 			});

	// 			const overlay = document.createElement('div');
	// 			overlay.style.cssText = `
	//             position: absolute;
	//             top: 0; left: 0;
	//             width: 100%; height: 100%;
	//             cursor: pointer;
	//             z-index: 30;
	//             background: transparent;
	//         `;

	// 			overlay.addEventListener('click', (e) => {
	// 				e.preventDefault();
	// 				e.stopPropagation();
	// 				const originalInput = originalThead.querySelector(
	// 					'.selectionHeader input[type="checkbox"]'
	// 				) as HTMLInputElement;
	// 				if (originalInput) {
	// 					originalInput.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true, view: window }));
	// 					originalInput.dispatchEvent(new MouseEvent('mouseup', { bubbles: true, cancelable: true, view: window }));
	// 					originalInput.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
	// 				}
	// 				const originalBox = originalThead.querySelector('.selectionHeader .p-checkbox-box') as HTMLElement;
	// 				if (originalBox) {
	// 					originalBox.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
	// 				}
	// 				const originalWrapper = originalThead.querySelector('.selectionHeader .p-checkbox') as HTMLElement;
	// 				if (originalWrapper) {
	// 					originalWrapper.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
	// 				}
	// 			});

	// 			cloneSelectionTh.appendChild(overlay);
	// 		}

	// 		// ── Wrap in a div tagged for clip reference ───────────────────────────
	// 		const clipWrapper = document.createElement('div');
	// 		clipWrapper.setAttribute('data-clip-wrapper', 'true');
	// 		clipWrapper.style.cssText = `
	//         position: relative;
	//         overflow: hidden;
	//         width: 100%;
	//     `;
	// 		clipWrapper.appendChild(cloneTable);
	// 		fixedContainer.appendChild(clipWrapper);
	// 		document.body.appendChild(fixedContainer);
	// 		stickyTable = fixedContainer;

	// 		// Hide original thead
	// 		originalThead.style.visibility = 'hidden';
	// 		originalThead.style.opacity = '0';
	// 		originalThead.style.pointerEvents = 'none';

	// 		// ── Checkbox visual sync ──────────────────────────────────────────────
	// 		const cloneCheckboxWrapper = cloneTable.querySelector('.selectionHeader .p-checkbox') as HTMLElement;
	// 		const cloneCheckboxBox = cloneTable.querySelector('.selectionHeader .p-checkbox-box') as HTMLElement;

	// 		const syncCheckboxVisual = () => {
	// 			if (!containerRef.current || !cloneCheckboxWrapper || !cloneCheckboxBox) return;

	// 			const allRows = containerRef.current.querySelectorAll('.p-datatable-tbody .p-checkbox');
	// 			const highlightedRows = containerRef.current.querySelectorAll('.p-datatable-tbody .p-checkbox.p-highlight');
	// 			const total = allRows.length;
	// 			const checked = highlightedRows.length;

	// 			if (total === 0 || checked === 0) {
	// 				cloneCheckboxWrapper.classList.remove('p-highlight');
	// 				cloneCheckboxBox.classList.remove('p-highlight');
	// 				cloneCheckboxBox.style.backgroundColor = '';
	// 				cloneCheckboxBox.style.borderColor = '';
	// 				cloneCheckboxBox.innerHTML = '';
	// 			} else if (checked === total) {
	// 				cloneCheckboxWrapper.classList.add('p-highlight');
	// 				cloneCheckboxBox.classList.add('p-highlight');
	// 				cloneCheckboxBox.style.backgroundColor = '#6366f1';
	// 				cloneCheckboxBox.style.borderColor = '#6366f1';
	// 				cloneCheckboxBox.innerHTML = `
	//                 <svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" style="width:14px;height:14px;display:block;">
	//                     <path d="M11.6666 3.5L5.24992 9.91667L2.33325 7"
	//                         stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
	//                 </svg>
	//             `;
	// 			} else {
	// 				// 	cloneCheckboxWrapper.classList.add('p-highlight');
	// 				// 	cloneCheckboxBox.classList.add('p-highlight');
	// 				// 	cloneCheckboxBox.style.backgroundColor = '#6366f1';
	// 				// 	cloneCheckboxBox.style.borderColor = '#6366f1';
	// 				// 	cloneCheckboxBox.innerHTML = `
	// 				//     <svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" style="width:14px;height:14px;display:block;">
	// 				//         <path d="M2.33325 7H11.6666"
	// 				//             stroke="white" stroke-width="2" stroke-linecap="round"/>
	// 				//     </svg>
	// 				// `;

	// 				cloneCheckboxWrapper.classList.remove('p-highlight');
	// 				cloneCheckboxBox.classList.remove('p-highlight');
	// 				cloneCheckboxBox.style.backgroundColor = '';
	// 				cloneCheckboxBox.style.borderColor = '';
	// 				cloneCheckboxBox.innerHTML = '';
	// 			}
	// 		};

	// 		syncCheckboxVisual();
	// 		// const pollInterval = setInterval(syncCheckboxVisual, 80);
	// 		// (stickyTable as any)._pollInterval = pollInterval;

	// 		const tbody = containerRef.current?.querySelector('.p-datatable-tbody') as HTMLElement;
	// 		const observer = new MutationObserver(() => {
	// 			requestAnimationFrame(syncCheckboxVisual);
	// 		});
	// 		if (tbody) {
	// 			observer.observe(tbody, {
	// 				attributes: true,
	// 				attributeFilter: ['class'],
	// 				subtree: true,
	// 			});
	// 		}
	// 		(stickyTable as any)._checkboxObserver = observer;

	// 		wrapper.addEventListener('scroll', syncHorizontalScroll, { passive: true });
	// 	};

	// 	const removeSticky = () => {
	// 		const existing = document.querySelector('[data-sticky-table]') as any;
	// 		if (existing?._checkboxObserver) existing._checkboxObserver.disconnect();
	// 		if (existing?._pollInterval) clearInterval(existing._pollInterval);
	// 		document.querySelector('[data-sticky-table]')?.remove();
	// 		stickyTable = null;
	// 		if (!containerRef.current) return;
	// 		const originalThead = containerRef.current.querySelector('.p-datatable-thead') as HTMLElement;
	// 		if (originalThead) {
	// 			originalThead.style.visibility = 'visible';
	// 			originalThead.style.opacity = '1';
	// 			originalThead.style.pointerEvents = '';
	// 		}
	// 		const wrapper = containerRef.current.querySelector('.p-datatable-wrapper') as HTMLElement;
	// 		wrapper?.removeEventListener('scroll', syncHorizontalScroll);
	// 	};

	// 	const handleScroll = () => {
	// 		if (!containerRef.current) return;
	// 		const originalThead = containerRef.current.querySelector('.p-datatable-thead') as HTMLElement;
	// 		if (!originalThead) return;

	// 		const tableRect = containerRef.current.getBoundingClientRect();
	// 		const theadHeight = originalThead.getBoundingClientRect().height;

	// 		if (tableRect.top <= NAVBAR_HEIGHT && tableRect.bottom > NAVBAR_HEIGHT + theadHeight) {
	// 			if (!stickyTable) {
	// 				createStickyHeader();
	// 			} else {
	// 				const containerRect = containerRef.current.getBoundingClientRect();
	// 				stickyTable.style.left = `${containerRect.left}px`;
	// 				stickyTable.style.width = `${containerRect.width}px`;
	// 				syncHorizontalScroll();
	// 			}
	// 		} else {
	// 			if (stickyTable) removeSticky();
	// 		}
	// 	};

	// 	const handleResize = () => { if (stickyTable) removeSticky(); };

	// 	window.addEventListener('scroll', handleScroll, { passive: true });
	// 	window.addEventListener('resize', handleResize);

	// 	return () => {
	// 		window.removeEventListener('scroll', handleScroll);
	// 		window.removeEventListener('resize', handleResize);
	// 		removeSticky();
	// 	};
	// }, [columnsKey]);

	// Stable per-instance id so the shared coordinator can tell grids apart
	const gridInstanceId = useRef(
		`payroll-grid-${Math.random().toString(36).slice(2)}`
	).current;

	useEffect(() => {
		registerStickyGrid({
			id: gridInstanceId,
			getContainerEl: () => containerRef.current,
			getScrollSourceEl: () =>
				pageScrollContainerRef?.current ||
				(containerRef.current?.closest(".payroll-grids-scroll-container") as HTMLElement | null),
			getFrozenColumnsEnabled: () => enableFrozenColumnsRef.current,
		});

		return () => {
			unregisterStickyGrid(gridInstanceId);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Force a re-evaluation when columns change (widths/visibility), same trigger as before
	useEffect(() => {
		refreshStickyGrid(gridInstanceId);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [columnsKey, pageScrollContainerRef]);

	// Keep your existing header-title tooltip logic — unrelated to the sticky singleton
	useEffect(() => {
		if (containerRef.current) {
			const originalThs = containerRef.current.querySelectorAll(
				'.p-datatable-thead > tr > th'
			) as NodeListOf<HTMLElement>;

			originalThs.forEach((th) => {
				const titleSpan = th.querySelector('.p-column-title') as HTMLElement;
				if (titleSpan) {
					const prevMaxWidth = titleSpan.style.maxWidth;
					titleSpan.style.maxWidth = 'none';
					const naturalWidth = titleSpan.scrollWidth;
					titleSpan.style.maxWidth = prevMaxWidth;

					th.title = naturalWidth > 120 ? titleSpan.textContent || '' : '';
				}
			});
		}
	}, [columnsKey]);



	// useEffect(() => {
	// 	if (isInitialClearRender.current) return;

	// 	requestAnimationFrame(() => {
	// 		const stickyCheckboxWrapper = document.querySelector(
	// 			'[data-sticky-table] .selectionHeader .p-checkbox'
	// 		) as HTMLElement | null;

	// 		const stickyCheckboxBox = document.querySelector(
	// 			'[data-sticky-table] .selectionHeader .p-checkbox-box'
	// 		) as HTMLElement | null;

	// 		if (!stickyCheckboxWrapper || !stickyCheckboxBox) return;

	// 		stickyCheckboxWrapper.classList.remove("p-highlight");
	// 		stickyCheckboxBox.classList.remove("p-highlight");

	// 		stickyCheckboxBox.style.backgroundColor = "";
	// 		stickyCheckboxBox.style.borderColor = "";
	// 		stickyCheckboxBox.innerHTML = "";
	// 	});
	// }, [clearAllSelectedItemsForCalculatedPayrollTable]);

	useEffect(() => {
		if (isInitialClearRender.current) return;
		clearStickyCheckboxHighlight();
	}, [clearAllSelectedItemsForCalculatedPayrollTable]);



	const selectedEmployeeIds = useMemo(
		() =>
			new Set(
				selectedItems?.map(
					(item: any) => item.employeeId
				)
			),
		[selectedItems]
	);

	// const renderColumnBody = useCallback(
	// 	(column: any) => {
	// 		return (rowData: any, row: any) => {
	// 			if (isLoading) {
	// 				if (selectedItems && selectedItems.length > 0) {
	// 					const isSelected = selectedEmployeeIds.has(rowData.employeeId);
	// 					return isSelected
	// 						? <Box sx={{ height: "38px", alignItems: "center", display: "flex" }}><Skeleton height="1.4rem" /></Box>
	// 						: columnsBody(rowData, row, column);
	// 				}
	// 				return <Skeleton height="1.5rem" />;
	// 			}
	// 			return columnsBody(rowData, row, column);
	// 		};
	// 	},
	// 	[isLoading, selectedItems, selectedEmployeeIds, columnsBody]
	// );

	const columnBodies = useMemo(() => {
		const map = new Map();
		for (const column of sortedColumns) {
			map.set(column.field, (rowData: any, row: any) => columnsBody(rowData, row, column));
		}
		return map;
	}, [sortedColumns, columnsBody]);

	const getHeaderClassName = useCallback(
		(column: any) => {
			if (
				column.field === "vacationPay" ||
				column.field === "vacationPayAccured"
			) {
				return "vacationPayField";
			}

			if (
				column.field === "holidayHours" ||
				column.field === "holidayPayAccured" ||
				column.field === "holidayPay"
			) {
				return "holidayHoursField";
			}

			if (column.edr === "earnings") {
				return "earningsField";
			}

			if (column.edr === "deductions") {
				return "deductionsField";
			}

			if (column.edr === "reimbursements") {
				return "reimbursementsField";
			}

			return "";
		},
		[]
	);

	const getBodyClassName = useCallback(
		(rowData: any, column: any) => {
			if (rowData.salaryProfileMessage) {
				return "";
			}

			return getHeaderClassName(column);
		},
		[getHeaderClassName]
	);

	const payrollGridClassName = useMemo(() => {
		const classes = ["CalculatedPayrollDataGrid"];

		if (isLoading) {
			if (selectedItems?.length > 0) {
				classes.push("payroll-grid-loading-selected");
			} else {
				classes.push("payroll-grid-loading-all");
			}
		}

		return classes.join(" ");
	}, [isLoading, selectedItems?.length]);


	const frozenColumnOffsets = useMemo(() => {
		const offsets = new Map<string, number>();
		let cumulativeLeft = 0;

		if (enableFrozenColumns && showChecboxColumn) {
			cumulativeLeft += 36; // selection column width
		}

		sortedColumns.forEach((col: any) => {
			if (enableFrozenColumns && col.frozenColumn) {
				offsets.set(col.field, cumulativeLeft);
				cumulativeLeft += Number(col.maxWidth) || 0;
			}
		});

		return offsets;
	}, [sortedColumns, enableFrozenColumns]);

	// this is for select all checkbox in footer bottom
	const handleSelectAllCheckbox = useCallback(
		(e: any) => {
			const isSelectAllChecked = e.checked;
			const allRowsSelected = rows || [];
			const allIds = allRowsSelected.map((item: any) => item.id);
			setManualSelectAll(isSelectAllChecked);
			setSelectedItems(isSelectAllChecked ? allRowsSelected : []);
			setSelectedItemsIds(isSelectAllChecked ? allIds : []);
		},

		[rows]
	);

	// When Save/Calculate kicks off a partial-selection loading state (skeleton
	// rows), the person likely scrolled down to reach the toolbar and can no
	// longer see the rows they selected. Scroll the FIRST selected row back
	// into view so they can see it go into (and come out of) the loading state.
	const prevIsLoadingRef = useRef(isLoading);

	useEffect(() => {
		const justStartedLoading = isLoading && !prevIsLoadingRef.current;
		prevIsLoadingRef.current = isLoading;

		if (!justStartedLoading) return;
		if (!selectedItems || selectedItems.length === 0) return;
		// Only for a PARTIAL selection — if all rows are selected, the person
		// already knows what's loading (the whole grid) and needs no scroll cue.
		if (!rows || selectedItems.length >= rows.length) return;

		const firstSelectedEmployeeId = selectedItems[0]?.employeeId;
		if (firstSelectedEmployeeId == null) return;

		const container = containerRef.current;
		if (!container) return;

		// Wait one frame so the loading/skeleton classes have actually painted
		// before we measure position.
		requestAnimationFrame(() => {
			const cell = container.querySelector(
				`[data-employee-id="${firstSelectedEmployeeId}"]`
			);
			const row = cell?.closest("tr");
			if (!row) return;

			const NAVBAR_HEIGHT = 112; // same constant as stickyHeaderManager.ts
			const EXTRA_GAP = 50;

			const rowRect = row.getBoundingClientRect();
			const targetTop =
				window.scrollY + rowRect.top - NAVBAR_HEIGHT - EXTRA_GAP;

			window.scrollTo({ top: targetTop, behavior: "smooth" });
		});
	}, [isLoading, selectedItems, rows]);

	return (
		<div
			// ref={containerRef}
			// style={{
			// 	width: "100%",
			// 	overflow: "hidden", // container stays fixed
			// 	background: "#fff"
			// }}

			ref={containerRef}
			onFocusCapture={handleGridFocus}
			onClick={handleGridClick}
			onBlurCapture={handleGridBlur}
			onKeyDownCapture={handleGridKeyDown}
			style={{
				// width: "100%",
				background: "#fff",
				width: gridsMaxTotalWidth && gridsMaxTotalWidth !== 0 ? `${gridsMaxTotalWidth}px` : "100%",
				minWidth: gridsMaxTotalWidth && gridsMaxTotalWidth !== 0 ? `${gridsMaxTotalWidth}px` : "100%"
			}}
		>
			<DataTable
				// value={rows && columns && payrollItems}
				// responsiveLayout="scroll"
				// // editMode={managedBy !== 100 ? "cell" : undefined} //non-editable for managed by simplepay
				// showGridlines
				// resizableColumns
				// columnResizeMode="expand"
				// scrollable={enableFrozenColumns}
				// key={selectedPayPeriod && selectedPayPeriod.process}
				// // scrollHeight={tableScrollHeight ? "75vh" : ""}
				// // reorderableColumns
				// loading={isLoading ? isLoading : loading}
				// footer={footer ? footer : undefined}
				// emptyMessage="No Record found."
				// className="CalculatedPayrollDataGrid"
				// style={{
				// 	margin: `${tableMargin ? tableMargin : "14px 0.5rem 0.5rem 0"}`,
				// }}
				// selection={selectedItems}
				// onSelectionChange={onSelectionModelChange}
				// // selectionMode="multiple"
				// selectionMode="checkbox"
				// sortMode="single"
				// /** 👇 Force fixed table layout so width styles are respected */
				// // tableStyle={{ tableLayout: 'fixed', width: '100%' }}
				// // tableStyle={{
				// // 	tableLayout: 'fixed',
				// // 	width: totalColumnWidth < containerWidth ? `${totalColumnWidth}px` : '100%',
				// // }}
				// tableStyle={{
				// 	tableLayout: totalColumnWidth < containerWidth ? "fixed" : "auto",
				// 	width: totalColumnWidth < containerWidth ? `${totalColumnWidth}px` : "100%"
				// }}
				// // onCellClick={(e) => alert("clicked")}
				// rowClassName={getRowClassName}

				value={payrollItems}
				dataKey="employeeId"
				// responsiveLayout="scroll"
				// showGridlines
				// resizableColumns
				// columnResizeMode="expand"
				// scrollable
				// key={isLoading}
				// footer={footer ? footer : undefined}
				emptyMessage={
					// rows && rows.length === 0 ? "No Record Found." : "Loading..."
					rows && rows.length === 0 ? "Loading..." : ""
				}
				className={payrollGridClassName}
				style={{
					margin: `${tableMargin ? tableMargin : "14px 0.5rem 0.5rem 0"}`,
				}}
				selection={selectedItems}
				onSelectionChange={onSelectionModelChange}
				selectionMode="checkbox"
				sortMode="single"
				tableStyle={
					useFitContainerLogic
						? {
							tableLayout: shouldFitContainer ? "fixed" : "auto",
							width: shouldFitContainer
								? `${totalColumnWidth}px`
								: "100%",
						}
						: {
							tableLayout: "fixed",
							// width: "100%",
							width: `${totalColumnWidth}px`,
						}
				}

			// tableStyle={{
			// 	tableLayout: "fixed",
			// 	width: "100%",
			// }}
			// onSort={onSort}

			>
				{columns && rows && (
					<Column
						selectionMode="multiple"
						hidden={!showChecboxColumn}
						// frozen={enableFrozenColumns}
						resizeable={false}
						// style={{ width: "36px", textAlign: "center" }}
						style={{
							width: "36px",
							textAlign: "center",
							...(enableFrozenColumns
								? {
									position: "sticky" as const,
									left: "0px",
									// zIndex: 6, 
									background: "#fff"
								}
								: {}),
						}}
						field="selection"
						columnKey="selection"
						className="selectionHeader"
						headerClassName="selectionHeader"
						// this is for select all checkbox in footer bottom
						footer={() =>
							showChecboxColumn ? (
								<div>
									<Checkbox
										checked={manualSelectAll}
										onChange={(e: any) =>
											handleSelectAllCheckbox({ checked: e.target.checked })
										}
									/>
								</div>
							) : (
								<div />
							)
						}
					></Column>
				)}
				{sortedColumns.map((x: any, index: any) => {
					const isFrozen = enableFrozenColumns && x.frozenColumn;
					const frozenLeft = frozenColumnOffsets.get(x.field) ?? 0;
					return (
						<Column
							key={index}
							field={x.field}
							header={x.headerName}
							// headerTooltip={x.description}
							// headerTooltipOptions={{ position: "top" }}
							// hidden={x.hide}
							// // sortable={x.sortable}
							// reorderable={false}
							// dataType={x.type}
							align={x.align}
							// alignHeader={x.headerAlign}
							// exportable={x.disableExport}
							// // frozen={totalColumnWidth < containerWidth ? false : x.frozenColumn}
							// frozen={enableFrozenColumns ? x.frozenColumn : false}
							// alignFrozen={x.alignFrozen}
							columnKey={x.field}
							// style={{
							// 	minWidth: x.minWidth,
							// 	fontSize: "0.875rem",
							// 	width: x.maxWidth,
							// 	maxWidth: x.maxWidth,
							// 	// width: x.maxWidth,
							// }}
							style={{
								minWidth: x.minWidth,
								fontSize: "0.875rem",
								width: x.maxWidth,
								maxWidth: x.maxWidth,
								...(isFrozen
									? {
										position: "sticky" as const,
										left: `${frozenLeft}px`,
										// zIndex: 5, 
										background: "#fff"
									}
									: {}),
							}}

							// headerStyle={{
							// 	whiteSpace: whiteSpace ? whiteSpace : "nowrap",
							// }}
							headerStyle={{
								whiteSpace: whiteSpace ? whiteSpace : "nowrap",
								...(isFrozen
									? {
										position: "sticky" as const,
										left: `${frozenLeft}px`,
										// zIndex: 6, 
										background: "#fff"
									}
									: {}),
							}}
							headerClassName={
								x.field === 'vacationPay' ? 'vacationPayField' :
									x.field === 'vacationPayAccured' ? 'vacationPayField' :
										x.field === 'holidayHours' ? 'holidayHoursField' :
											x.field === 'holidayPayAccured' ? 'holidayHoursField' :
												x.field === 'holidayPay' ? 'holidayHoursField' :
													x.edr === 'earnings' ? 'earningsField' :
														x.edr === 'deductions' ? 'deductionsField' :
															x.edr === 'reimbursements' ? 'reimbursementsField' :
																isFrozen ? ' sticky-frozen-th' :
																	''}
							// className={
							// 	x.field === 'vacationPay' ? 'vacationPayField' :
							// 		x.field === 'vacationPayAccured' ? 'vacationPayField' :
							// 			x.field === 'holidayHours' ? 'holidayHoursField' :
							// 				x.field === 'holidayPayAccured' ? 'holidayHoursField' :
							// 					x.field === 'holidayPay' ? 'holidayHoursField' :
							// 						x.edr === 'earnings' ? 'earningsField' :
							// 							x.edr === 'deductions' ? 'deductionsField' :
							// 								x.edr === 'reimbursements' ? 'reimbursementsField' :
							// 									''}
							footer={x.footer}
							// body={(rowData: any, row: any) =>
							// 	columnsBody(rowData, row, x)
							// }
							// body={renderColumnBody(x)}
							body={columnBodies.get(x.field)}
						/>
					);
				})}
			</DataTable>
		</div>
	);
};
